import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AlertCode } from 'shared/types/checkout';
import { AlertBanner, AlertTypes } from 'components/ui/atoms';
import { PageLoader } from 'components/ui/atoms/loader/page-loader';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'components/ui/atoms/pagination';
import { DEFAULT_PAGE_SIZE } from 'helpers/constants';
import { generatePageNumbers } from 'utils/pagination';
import { useDiscountedProducts } from 'frontastic/hooks/useProducts';
import { getAlertMessage } from '../checkout/components/alert.messages';
import {
  DiscountFooter,
  DiscountProductCard,
  DiscountProductCardSkeleton,
} from './components';
import { messages } from './messages';
import { RedeemDiscountProps } from './types';

export const RedeemDiscount = ({ discountCode }: RedeemDiscountProps) => {
  const { formatMessage } = useIntl();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: productResponse,
    isLoading,
    isError: isGeneralError,
  } = useDiscountedProducts(discountCode);
  const { products, error, isError: isApiError } = productResponse;

  const totalCount = products.length;
  const isError = isGeneralError || isApiError;
  const pageCount = Math.ceil(products.length / DEFAULT_PAGE_SIZE);

  const handlePageSelect = (page: number) => {
    setCurrentPage(page);
  };

  const renderedCourses = useMemo(() => {
    const pageOffset = (currentPage - 1) * DEFAULT_PAGE_SIZE;
    return products
      .slice(pageOffset, pageOffset + DEFAULT_PAGE_SIZE)
      .map((product) => (
        <article
          key={product.courseRunKey}
          className="tw-p-4 [&>div]:tw-h-full"
        >
          <DiscountProductCard
            product={product}
            discountCode={discountCode}
            isLoading={isLoading}
          />
        </article>
      ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, currentPage]);

  const renderCardSkeleton = () =>
    Array.from({ length: 6 }).map((key) => (
      <article
        key={`skeleton-${key as number}`}
        className="tw-p-4 [&>div]:tw-h-full"
      >
        <DiscountProductCardSkeleton />
      </article>
    ));

  const renderPagination = () => {
    const pageNumbers = generatePageNumbers(currentPage, pageCount);
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === pageCount;

    return (
      <div className="tw-w-full tw-flex tw-justify-center tw-pt-4 md:tw-justify-end">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                disabled={isFirstPage}
                onClick={() =>
                  !isFirstPage && handlePageSelect(currentPage - 1)
                }
                text="Previous"
              />
            </PaginationItem>
            {pageNumbers.map((page, index) => {
              if (page === 'ellipsis') {
                const prevPage = pageNumbers[index - 1];
                const nextPage = pageNumbers[index + 1];
                return (
                  <PaginationItem
                    key={`ellipsis-${prevPage}-${nextPage}-${currentPage}`}
                  >
                    <PaginationEllipsis />
                  </PaginationItem>
                );
              }
              return (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={page === currentPage}
                    onClick={() => handlePageSelect(page)}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                disabled={isLastPage}
                onClick={() => !isLastPage && handlePageSelect(currentPage + 1)}
                text="Next"
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  const renderContent = () => (
    <section className="tw-grid -tw-mx-4 min-[992px]:tw-grid-cols-[1fr_0.35fr] min-[1200px]:tw-grid-cols-1">
      <div className="tw-mb-8 tw-p-4">
        <h1 className="tw-text-xl">
          {formatMessage(messages['redeem.discount.page.heading'])}
        </h1>
        <p className="tw-mb-4">
          {formatMessage(messages['redeem.discount.page.subheading'])}
        </p>
        <hr />
        <section className="tw-grid -tw-mx-4 tw-mt-6 min-[1200px]:tw-grid-cols-2">
          {isLoading ? renderCardSkeleton() : renderedCourses}
        </section>
        {totalCount > DEFAULT_PAGE_SIZE && renderPagination()}
      </div>
      <DiscountFooter />
    </section>
  );

  const renderAlert = (alertCode: AlertCode) => (
    <AlertBanner
      variant={AlertTypes.DANGER}
      onClose={() => {
        window.location.replace(
          process.env.NEXT_PUBLIC_EDX_LEARNER_DASHBOARD_URL!,
        );
      }}
    >
      {getAlertMessage(alertCode, discountCode)}
    </AlertBanner>
  );

  if (isLoading && !totalCount) {
    return (
      <PageLoader
        loaderText={formatMessage(messages['redeem.discount.page.loader'])}
      />
    );
  }

  if (isError || !totalCount) {
    return (
      <div className="container-fluid py-5">
        {renderAlert(isError ? error : AlertCode.PRODUCT_NOT_FOUND_FOR_COUPON)}
      </div>
    );
  }

  return (
    <div className="container-fluid container-mw-lg">{renderContent()}</div>
  );
};
